/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useMemo
} from 'react';
import {
  useQuery,
  useQueryClient
} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {loadSentinelInfo} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  getStaleTime
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceSentinelInfoResult = UseDefaultQueryResult & {
  sentinelInfo: GenericObject;
  refresh: () => void;
};

/**
 * Retrieves Sentinel information of the device with the given identifier.
 * @param deviceIdentifier the identifier of the device (either the esn or serial number).
 */
const getDeviceSentinelInfo = async(deviceId: string | number): Promise<GenericObject | null> => {
  try {
    const SentinelInfo = await loadSentinelInfo({deviceId});
    return SentinelInfo;
  } catch (err) {
    error(`An Exception occurred retrieving Sentinel Info for device ${deviceId}. ${err?.toString()}`);
  }
};

/**
 * Retrieves the Sentinel information for the device of the given identifier.
 *
 * @param deviceIdentifier identifier of the device to retrieve information for (esn or serial number)
 * @param enabled if the query is enabled / disabled
 * @returns a query result
 */
export const useDeviceSentinelInfo = (
  deviceIdentifier: string | number,
  enabled = true
): UseDeviceSentinelInfoResult => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (
    createKey(QueryKey.DEVICE_SENTINEL_INFO, deviceIdentifier)
  ), [deviceIdentifier]);

  const query = useQuery(key, async() => {
    const results = await getDeviceSentinelInfo(deviceIdentifier);
    return results;
  }, {
    ...getStaleTime(5) as DurationResult,
    enabled
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    sentinelInfo : query?.data || {},
    refresh
  }) as UseDeviceSentinelInfoResult;
};

export default useDeviceSentinelInfo;
